'use client'

import { useState, useEffect, useMemo } from 'react'
import { useAuth } from '@/store/hooks'
import {
    createAttendant,
    updateAttendant,
    getAttendant,
    getAttendantCapabilities,
    AiCapability,
} from '@/lib/api/atendimento/attendants'
import { getDepartmentsByOrganization } from '@/lib/api/atendimento/departments'
import { Department } from '@/lib/api/common/interfaces/department.interface'
import { toast } from 'react-toastify'
import { useNavigate, useParams } from 'react-router-dom'
import { MdArrowBack, MdRule, MdAutoAwesome, MdTune } from 'react-icons/md'
import { useClientLabel } from '@/lib/hooks/use-client-label'

export default function AttendantsFormPage() {
    const { user, isPartner } = useAuth()
    const { clientLabel } = useClientLabel()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditing = !!id
    const [loading, setLoading] = useState(false)
    const [loadingAttendant, setLoadingAttendant] = useState(false)
    const [departments, setDepartments] = useState<Department[]>([])
    const [capabilities, setCapabilities] = useState<AiCapability[]>([])

    const [formData, setFormData] = useState({
        name: '',
        rules: '',
        departmentId: '',
        enabledCapabilities: [] as string[],
    })

    useEffect(() => {
        if (user?.organizationId) {
            loadDepartments()
        }
    }, [user?.organizationId])

    useEffect(() => {
        loadCapabilities()
    }, [])

    useEffect(() => {
        if (isEditing && id) {
            loadAttendant()
        }
    }, [id, isEditing])

    // Agrupa as capacidades por area para exibir na UI
    const groupedCapabilities = useMemo(() => {
        const groups: Record<string, AiCapability[]> = {}
        for (const cap of capabilities) {
            ;(groups[cap.group] ??= []).push(cap)
        }
        return groups
    }, [capabilities])

    const loadCapabilities = async () => {
        try {
            const response = await getAttendantCapabilities()
            if (response.success) {
                setCapabilities(response.result)
                // Em agente novo, pré-marca as capacidades essenciais (removíveis)
                if (!isEditing) {
                    const essentials = response.result
                        .filter(c => c.essential && c.available)
                        .map(c => c.key)
                    setFormData(prev =>
                        prev.enabledCapabilities.length === 0
                            ? { ...prev, enabledCapabilities: essentials }
                            : prev
                    )
                }
            }
        } catch (error) {
            console.error('Erro ao carregar capacidades:', error)
        }
    }

    const toggleCapability = (key: string) => {
        setFormData(prev => {
            const set = new Set(prev.enabledCapabilities)
            if (set.has(key)) {
                set.delete(key)
            } else {
                set.add(key)
            }
            return { ...prev, enabledCapabilities: Array.from(set) }
        })
    }

    const loadDepartments = async () => {
        const organizationId = user?.organizationId
        if (!organizationId) return

        try {
            const response = await getDepartmentsByOrganization(
                organizationId,
                isPartner ? user.partnerId : undefined
            )
            if (response.success) {
                setDepartments(response.result)
            }
        } catch (error) {
            console.error('Erro ao carregar setores:', error)
        }
    }

    const loadAttendant = async () => {
        if (!id) return

        setLoadingAttendant(true)
        try {
            const response = await getAttendant(parseInt(id, 10))
            if (response.success) {
                const attendant = Array.isArray(response.result)
                    ? response.result[0]
                    : response.result
                if (attendant) {
                    setFormData({
                        name: attendant.name,
                        rules: attendant.rules,
                        departmentId: attendant.departmentId.toString(),
                        enabledCapabilities:
                            attendant.enabledCapabilities ?? [],
                    })
                } else {
                    toast.error('Atendente não encontrado')
                    navigate('/atendimento/agents')
                }
            } else {
                toast.error(response.error || 'Erro ao carregar atendente')
                navigate('/atendimento/agents')
            }
        } catch (error) {
            toast.error('Erro ao carregar atendente')
            navigate('/atendimento/agents')
        } finally {
            setLoadingAttendant(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        const organizationId = user?.organizationId
        if (!organizationId) {
            toast.error('Usuário sem organização')
            return
        }

        if (!formData.name.trim()) {
            toast.warning('Preencha o nome do atendente')
            return
        }

        if (!formData.rules.trim()) {
            toast.warning('Preencha as regras')
            return
        }

        if (!formData.departmentId) {
            toast.warning('Selecione um setor')
            return
        }

        setLoading(true)

        try {
            let response

            if (isEditing) {
                response = await updateAttendant(parseInt(id!, 10), {
                    name: formData.name.trim(),
                    rules: formData.rules.trim(),
                    departmentId: Number(formData.departmentId),
                    enabledCapabilities: formData.enabledCapabilities,
                })
            } else {
                response = await createAttendant({
                    name: formData.name.trim(),
                    rules: formData.rules.trim(),
                    departmentId: Number(formData.departmentId),
                    enabledCapabilities: formData.enabledCapabilities,
                    organizationId,
                    ...(isPartner && user.partnerId ? { partnerId: user.partnerId } : {}),
                })
            }

            if (response.success) {
                toast.success(
                    isEditing
                        ? 'Atendente atualizado com sucesso'
                        : 'Atendente criado com sucesso'
                )
                navigate('/atendimento/agents')
            } else {
                toast.error(
                    response.error ||
                        (isEditing
                            ? 'Erro ao atualizar atendente'
                            : 'Erro ao criar atendente')
                )
            }
        } catch (error) {
            toast.error(
                isEditing
                    ? 'Erro ao atualizar atendente'
                    : 'Erro ao criar atendente'
            )
        } finally {
            setLoading(false)
        }
    }

    if (loadingAttendant) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando atendente...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/atendimento/agents')}
                    className="btn btn-ghost btn-circle btn-sm"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-info to-info/70 flex items-center justify-center shadow-lg">
                        <MdAutoAwesome className="text-2xl text-white" />
                    </div>
                    <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                        {isEditing
                            ? 'Editar Atendente IA'
                            : 'Novo Atendente IA'}
                    </h1>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                <div className="form-control w-full">
                    <label className="label">
                        <span className="label-text">
                            Setor <span className="text-error">*</span>
                        </span>
                    </label>
                    <select
                        className="select select-bordered w-full"
                        value={formData.departmentId}
                        onChange={e =>
                            setFormData({
                                ...formData,
                                departmentId: e.target.value,
                            })
                        }
                        required
                    >
                        <option value="">Selecione um setor</option>
                        {departments.map(dept => (
                            <option key={dept.id} value={dept.id}>
                                {dept.name}
                            </option>
                        ))}
                    </select>
                    {departments.length === 0 && (
                        <label className="label">
                            <span className="label-text-alt text-warning">
                                Nenhum setor cadastrado. Cadastre um setor
                                primeiro.
                            </span>
                        </label>
                    )}
                </div>

                <div className="form-control w-full">
                    <label className="label">
                        <span className="label-text">
                            Nome <span className="text-error">*</span>
                        </span>
                    </label>
                    <input
                        type="text"
                        placeholder="Nome do atendente"
                        className="input input-bordered w-full"
                        value={formData.name}
                        onChange={e =>
                            setFormData({ ...formData, name: e.target.value })
                        }
                        required
                    />
                </div>

                <div className="form-control w-full">
                    <label className="label">
                        <span className="label-text flex items-center gap-2">
                            <MdRule />
                            Regras para o atendente{' '}
                            <span className="text-error">*</span>
                        </span>
                    </label>
                    <textarea
                        className="textarea textarea-bordered w-full"
                        placeholder={`Ex: Sempre cumprimentar o ${clientLabel}. Responder em até 2 horas.`}
                        value={formData.rules}
                        onChange={e =>
                            setFormData({
                                ...formData,
                                rules: e.target.value,
                            })
                        }
                        rows={6}
                        required
                    />
                    <label className="label">
                        <span className="label-text-alt text-base-content/60">
                            Ensine ou treine o atendente. Estas regras têm
                            precedência sobre o comportamento padrão da IA:
                            havendo conflito, elas é que valem
                        </span>
                    </label>
                </div>

                <div className="bg-base-200/50 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-1">
                        <MdTune className="text-lg text-violet-500" />
                        <h2 className="text-lg font-semibold">
                            Capacidades do agente
                        </h2>
                    </div>
                    <p className="text-sm text-base-content/60 mb-4">
                        Escolha exatamente o que este agente pode fazer. Ele só
                        terá acesso às capacidades marcadas — nada além disso.
                    </p>

                    {capabilities.length === 0 ? (
                        <p className="text-sm text-base-content/60">
                            Carregando capacidades...
                        </p>
                    ) : (
                        <div className="flex flex-col gap-5">
                            {Object.entries(groupedCapabilities).map(
                                ([group, items]) => (
                                    <div key={group}>
                                        <h3 className="text-xs font-semibold uppercase tracking-wide text-base-content/50 mb-2">
                                            {group}
                                        </h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            {items.map(cap => {
                                                const checked =
                                                    formData.enabledCapabilities.includes(
                                                        cap.key
                                                    )
                                                // Indisponível só bloqueia marcar
                                                // uma capacidade nova; uma já
                                                // marcada continua removível.
                                                const locked =
                                                    !cap.available && !checked
                                                return (
                                                    <label
                                                        key={cap.key}
                                                        className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                                                            locked
                                                                ? 'border-base-300/50 bg-base-100/50 opacity-60 cursor-not-allowed'
                                                                : checked
                                                                  ? 'border-violet-500/50 bg-violet-500/5 cursor-pointer'
                                                                  : 'border-base-300/50 bg-base-100 hover:border-violet-500/30 cursor-pointer'
                                                        }`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            className="checkbox checkbox-secondary checkbox-sm mt-0.5"
                                                            checked={checked}
                                                            disabled={locked}
                                                            onChange={() =>
                                                                toggleCapability(
                                                                    cap.key
                                                                )
                                                            }
                                                        />
                                                        <div className="min-w-0">
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <span className="text-sm font-medium text-base-content">
                                                                    {cap.label}
                                                                </span>
                                                                {cap.essential && (
                                                                    <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-medium">
                                                                        Essencial
                                                                    </span>
                                                                )}
                                                                {!cap.available && (
                                                                    <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-medium">
                                                                        {cap.requiresPaymentAccount
                                                                            ? 'Requer conta digital ativa'
                                                                            : 'Indisponível'}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className="text-xs text-base-content/60 mt-0.5">
                                                                {cap.description}
                                                            </p>
                                                        </div>
                                                    </label>
                                                )
                                            })}
                                        </div>
                                    </div>
                                )
                            )}

                            {formData.enabledCapabilities.length === 0 && (
                                <p className="text-sm text-warning">
                                    Nenhuma capacidade marcada: este agente não
                                    conseguirá executar nenhuma ação.
                                </p>
                            )}
                        </div>
                    )}
                </div>

                <div className="flex gap-2 pt-4">
                    <button
                        type="button"
                        className="btn btn-ghost flex-1"
                        onClick={() => navigate('/atendimento/agents')}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary flex-1"
                        disabled={
                            loading ||
                            !formData.name.trim() ||
                            !formData.rules.trim() ||
                            !formData.departmentId
                        }
                    >
                        {loading ? (
                            <span className="loading loading-spinner"></span>
                        ) : isEditing ? (
                            'Atualizar'
                        ) : (
                            'Salvar'
                        )}
                    </button>
                </div>
            </form>
        </div>
    )
}
